import { getDistance as haversineDist } from 'ol/sphere';
import { legForEndPoint, pruneRouteGeometry, toDegrees, toRadians } from './helpers';
import { VoyageRoutePointFeature, VoyageRouteLineFeature, VoyageRoute } from '../models/voyage-routing-model';
import { VoyagePlan } from '../models/vessel-planning-model';
import { ImportResult } from './types/converters.types';

const NMToMeters = 1852; // Nautical miles -> meters

export class NacosConverter {
    static check(str: string) {
        try {
            const domParser = new DOMParser();
            var doc = domParser.parseFromString(str, "text/xml");

            const root = doc.firstElementChild;

            if (root && root.nodeName === 'RouteModel' &&
                root.getAttribute('xmlns') === 'http://www.sam-electronics.de/2010/reducedRouteModel.xsd') {
                return doc;
            }
        } catch(e) {
            // don't care
        }
    }

    static import(str: string, options = {}): ImportResult|undefined {
        const opt = {
            assumeGCAbove: 300,
            ...options
        }

        try {
            const route_geometry: Array<VoyageRoutePointFeature|VoyageRouteLineFeature>  = [];

            const doc = NacosConverter.check(str);
            if (!doc) return;
            const waypoints = doc.getElementsByTagName('Waypoints');
            let prevCoords: [number,number]|null = null;

            let sailMode: Element|null;
            Array.from(waypoints).forEach(waypoint => {
                const coords: [number, number] = [
                    toDegrees(Number.parseFloat(waypoint.querySelector('Longitude')!.textContent!)),
                    toDegrees(Number.parseFloat(waypoint.querySelector('Latitude')!.textContent!))
                ];

                const name = waypoint.querySelector('Name');

                const wPoint: VoyageRoutePointFeature = {
                    type: 'Feature',
                    geometry: {
                        coordinates: coords,
                        type: 'Point'
                    },
                    properties: {
                        type: 'Hard',
                        name: name ? name.textContent! : ''
                    }
                };

                if (prevCoords) {
                    const dist = haversineDist(prevCoords, coords) / NMToMeters; // Accept minimal error on dist, assuming GC for RL

                    let isGC = true;
                    if (sailMode) {
                        isGC = sailMode.textContent === '0';
                    } else if (dist < opt.assumeGCAbove) {
                        isGC = false;
                    }

                    route_geometry.push({
                            type: 'Feature',
                            geometry: {
                                coordinates: [prevCoords, coords],
                                type: 'LineString'
                            },
                            properties: {
                                leg_type: isGC ? 'GreatCircle' : 'Rhumbline'
                            }
                    });
                }

                route_geometry.push(wPoint);

                // The Nacos route has GC/RL marked on the source wp
                sailMode = waypoint.querySelector('SailMode');

                prevCoords = coords;
            });

            return {
                route: { route_geometry },
                extras: {
                    // Plan related data.

                }
            };
        } catch (error) {
            console.warn(error);
        }
    }

    static export(plan: VoyagePlan, options = {}): string
    {
        const opt = {
            ...options
        }

        // Bail out if no valid route is selected.
        if (plan.selectedRouteIndex < 0 || plan.selectedRouteIndex >= plan.routes.length)
        {
            throw new Error("No valid route selected in plan!");
        }

        // Only include the selected route.
        const route = plan.routes[plan.selectedRouteIndex];

        const xmlString = '<RouteModel xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:xsd="http://www.w3.org/2001/XMLSchema" xmlns="http://www.sam-electronics.de/2010/reducedRouteModel.xsd"/>';
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlString, "text/xml"); //important to use "text/xml"
        const routeModel = xmlDoc.documentElement;

        const routeName = document.createElementNS('http://www.sam-electronics.de/2010/reducedRouteModel.xsd', 'Name');
        routeModel.appendChild(routeName);
        routeName.setAttribute('xmlns','');

        const prunedRoute = pruneRouteGeometry(route.route_geometry);

        let points = <VoyageRoutePointFeature[]>prunedRoute.filter(item => item.geometry.type === "Point");

        const legs = <VoyageRouteLineFeature[]>prunedRoute.filter(item => item.geometry.type === "LineString");

        const addNSElement = (tagName: string, text?: string) => {
            const el = document.createElementNS('http://www.sam-electronics.de/2010/reducedRouteModel.xsd', tagName);
            if (text !== undefined) {
                el.appendChild(document.createTextNode(text));
            }
            return el;
        }

        points.forEach(point => {
            const wp = addNSElement('Waypoints');
            wp.setAttribute('xmlns','');
            
            wp.appendChild(addNSElement('Name', point.properties.name || ''));
            
            wp.appendChild(addNSElement('Latitude', `${toRadians(point.geometry.coordinates[1])}`));
            wp.appendChild(addNSElement('Longitude', `${toRadians(point.geometry.coordinates[0])}`));
            
            const leg = legForEndPoint(legs, point.geometry.coordinates, 0);
            let sailMode = '1';
            if (leg && leg.properties && leg.properties.leg_type === 'GreatCircle') sailMode = '0';
            wp.appendChild(addNSElement('SailMode', sailMode));

            wp.appendChild(addNSElement('Radius', '123'));
            
            routeModel.appendChild(wp);
        });

        const xmlSer = new XMLSerializer();
        const result = '<?xml version="1.0"?>\r\n' + xmlSer.serializeToString(xmlDoc);

        return result;
    }
}
